import { NotifyOptionsSuccess, NotifyOptionsError } from "@modernary/mui/components/notifications";

import { apiService, type Paging, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { apiHosts } from "@/api/constants/apiHosts";
import type { AxiosResponse } from "axios";
import type { DeliverableAcceptanceForm, DeliverableAcceptanceFormNew } from "@/views/project/project";

const URL = (projectId: string | number) => `projects/${projectId}/deliverableAcceptanceForms`;
const ENTITY_NAME = "Deliverable Acceptance Form";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_INSIGHT_API_BASE_URL,
};
const api = apiService<DeliverableAcceptanceForm, Paging<DeliverableAcceptanceForm>>(apiHosts.INSIGHT, axiosParams);

/**
 * @param {Object} params Query parameters, varies (TODO improve this)
 * @param {abortCallback} abortMethod Callback for when a request has been aborted
 */
const getDeliverableAcceptanceForms = (params: any, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifDb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL(params.projectId);

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {Int} id
 */
const getDeliverableAcceptanceFormById = (ids: any, config: PagingApiAndNotifierConfig = {}) => {
  //TODO:TS
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };
  return api.get(URL(ids.projectId) + "/" + ids.id, {
    successNotifCb,
    errorNotifCb,
  });
};

const getNewDeliverableAcceptanceForm = async (ids: any) => {
  //TODO:TS
  //optionally make any other changes as necessary before returning.
  return {
    data: { ...ids, deliverableAcceptanceFormItems: [{ key: -Date.now() }] },
  } as AxiosResponse<DeliverableAcceptanceFormNew, any>;
};

const createDeliverableAcceptanceForm = (
  deliverableAcceptanceForm: DeliverableAcceptanceForm,
  config: PagingApiAndNotifierConfig = {},
) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL(deliverableAcceptanceForm.projectId), deliverableAcceptanceForm, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateDeliverableAcceptanceForm = (
  deliverableAcceptanceForm: DeliverableAcceptanceForm,
  config: PagingApiAndNotifierConfig = {},
) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };
  return api.put(
    URL(deliverableAcceptanceForm.projectId) + "/" + deliverableAcceptanceForm.id,
    deliverableAcceptanceForm,
    {
      successNotifCb,
      errorNotifCb,
    },
  );
};

/**
 * @param {DeliverableAcceptanceForm} deliverableAcceptanceForm This cannot be a 'ref' to a deliverable acceptance form
 */
const saveDeliverableAcceptanceForm = (
  deliverableAcceptanceForm: DeliverableAcceptanceForm,
  config: PagingApiAndNotifierConfig = {},
) => {
  if (deliverableAcceptanceForm.id) {
    return updateDeliverableAcceptanceForm(deliverableAcceptanceForm, config);
  } else {
    return createDeliverableAcceptanceForm(deliverableAcceptanceForm, config);
  }
};

/**
 * @param {Int} id
 */
const deleteDeliverableAcceptanceFormById = (
  fa: DeliverableAcceptanceForm,
  config: PagingApiAndNotifierConfig = {},
) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL(fa.projectId) + "/" + fa.id, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  deleteDeliverableAcceptanceFormById,
  saveDeliverableAcceptanceForm,
  updateDeliverableAcceptanceForm,
  createDeliverableAcceptanceForm,
  getDeliverableAcceptanceFormById,
  getDeliverableAcceptanceForms,
  getNewDeliverableAcceptanceForm,
};
